import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
    filterChipBaseClass,
    filterChipNeutralClass,
    filterChipActiveClass,
    tableToolbarControlOpenClass,
} from '../lib/tableClasses';

/**
 * One filter pill of a `FilterBar`: «Estado: Pendientes ✕».
 *
 * Presentational only. `DropdownSelect` and `DateRangePicker` render it as
 * their trigger when `variant="chip"`, so the list, the calendar, the portal
 * and the keyboard handling stay the ones those components already have — the
 * chip is a different face on the same control, not a second implementation.
 *
 * The ✕ is a SIBLING button laid over the pill's right end, never a child of
 * the trigger: a button inside a button is invalid HTML and swallows the
 * click in some browsers.
 */
export interface FilterChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
    /** Filter name — rendered muted before the value. */
    label: string;
    /** What the filter is set to. */
    value: React.ReactNode;
    /** Material Symbol before the label. */
    icon?: IconName;
    /** Narrows the list (accent) vs. shown at its default (neutral). */
    active?: boolean;
    /** Its menu or calendar is open. */
    open?: boolean;
    /** When set, a ✕ removes the filter. Pinned filters leave it unset. */
    onRemove?: () => void;
    removeLabel?: string;
}

const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
    ({ label, value, icon, active = false, open = false, onRemove, removeLabel = 'Quitar filtro', className = '', ...rest }, ref) => (
        <span className="relative inline-flex min-w-0 shrink-0">
            <button
                ref={ref}
                type="button"
                {...rest}
                className={[
                    filterChipBaseClass,
                    active ? filterChipActiveClass : filterChipNeutralClass,
                    open ? tableToolbarControlOpenClass : '',
                    onRemove ? 'pr-8' : '',
                    className,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {icon ? (
                    <Icon name={icon} className="!text-[16px] shrink-0" aria-hidden="true" />
                ) : null}
                <span className={active ? 'opacity-80' : 'text-slate-500 dark:text-slate-400'}>{label}:</span>
                <span className="min-w-0 truncate">{value}</span>
                {onRemove ? null : (
                    <Icon name="expand_more" className={`!text-[16px] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                )}
            </button>
            {onRemove ? (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={`${removeLabel}: ${label}`}
                    title={removeLabel}
                    className={`absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                        active
                            ? 'text-primary hover:bg-primary/20'
                            : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                >
                    <Icon name="close" className="!text-[16px]" aria-hidden="true" />
                </button>
            ) : null}
        </span>
    ),
);
FilterChip.displayName = 'FilterChip';

export default FilterChip;
